import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ContactUs_API, ResumeDelete_API, BASE_URL } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaTrash, FaFilter, FaTimes } from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import "./JobModal.css"; // Reuse same modal CSS

export default function ContactUs() {
  const { userData } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    name: [],
    email: [],
    mobile: [],
    company: [],
    date: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const columnDisplay = {
    id: "ID",
    name: "Name",
    email: "Email",
    mobile: "Mobile",
    company: "Company",
    date: "Date",
  };

  // Fetch all contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await fetch(`${ContactUs_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch contacts");
        const data = await response.json();
        setContacts(data);
      } catch (error) {
        console.error("Error fetching contacts:", error);
        Swal.fire("Error", "Failed to fetch contacts.", "error");
      }
    };
    fetchContacts();
  }, [userData.access_token]);

  // Get distinct values for filter
  const getDistinctValues = (column) => {
    const values = contacts.map((item) => item[column]);
    const unique = [...new Set(values)].filter(v => v != null);
    return unique.sort((a, b) =>
      column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.name.length === 0 || filters.name.includes(item.name)) &&
      (filters.email.length === 0 || filters.email.includes(item.email)) &&
      (filters.mobile.length === 0 || filters.mobile.includes(item.mobile)) &&
      (filters.company.length === 0 || filters.company.includes(item.company)) &&
      (filters.date.length === 0 || filters.date.includes(item.date))
    );
  };

  const filteredContacts = contacts.filter(applyFilters);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredContacts.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredContacts.map((item) => item.id));
    }
  };

  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected = tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prev) => ({ ...prev, [showFilterPopup]: newSelected }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete attached file from server
  const deleteAttachedFile = async (path) => {
    if (!path) return;
    try {
      await fetch(`${ResumeDelete_API}?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${userData.access_token}` },
      });
    } catch (err) {
      console.error("Failed to delete file:", err);
    }
  };

  // Delete single contact
  const deleteContact = async (id) => {
    const result = await Swal.fire({
      text: "Do you want to delete this contact?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      const contact = contacts.find(c => c.id === id);
      if (contact?.file) await deleteAttachedFile(contact.file);

      const response = await fetch(`${ContactUs_API}/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${userData.access_token}`,
        },
      });

      if (!response.ok) throw new Error("Delete failed");

      setContacts(contacts.filter(c => c.id !== id));
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
      Swal.fire("Deleted!", "Contact deleted successfully.", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to delete contact.", "error");
    }
  };

  // Delete multiple
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    const result = await Swal.fire({
      text: `Delete ${selectedRows.length} selected contacts?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedRows.map(async (id) => {
          const contact = contacts.find(c => c.id === id);
          if (contact?.file) await deleteAttachedFile(contact.file);

          const res = await fetch(`${ContactUs_API}/delete/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${userData.access_token}` },
          });
          if (!res.ok) throw new Error(`Failed to delete ${id}`);
        })
      );

      setContacts(contacts.filter(c => !selectedRows.includes(c.id)));
      setSelectedRows([]);
      Swal.fire("Success!", "Selected contacts deleted.", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to delete some contacts.", "error");
    }
  };

  // Open modal
  const viewContact = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  // Filter popup same as before (unchanged)
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter((val) =>
      val?.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) => tempSelected.includes(val));

    const clearSelections = () => {
      setFilters((prev) => ({ ...prev, [column]: [] }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div className="filter-popup" style={{ position: "absolute", top: "100%", left: 0, zIndex: 100 }}>
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>Clear</span>
        </div>
        <div className="filter-search-container">
          <input type="checkbox" checked={allVisibleSelected} onChange={(e) => {
            if (e.target.checked) setTempSelected(filteredValues);
            else setTempSelected(tempSelected.filter(v => !filteredValues.includes(v)));
          }} />
          <input className="filter-search" type="text" placeholder="Search" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} />
          {searchInput && <span className="clear-search" onClick={() => setSearchInput("")}>×</span>}
        </div>
        <div className="suggestions-list">
          {filteredValues.map((val) => (
            <label key={val}>
              <input type="checkbox" checked={tempSelected.includes(val)}
                onChange={(e) => {
                  if (e.target.checked) setTempSelected([...tempSelected, val]);
                  else setTempSelected(tempSelected.filter(v => v !== val));
                }} />
              {val?.toString() || "N/A"}
            </label>
          ))}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet><title>Contact Forms - SMTS Admin Panel</title></Helmet>
      <div className="page-title"><h2>Contact Us</h2></div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input type="checkbox" checked={selectedRows.length === filteredContacts.length && filteredContacts.length > 0}
                  onChange={handleSelectAll} />
              </th>
              <th style={{ position: "relative" }}>ID
                <FaFilter className={`filter-icon ${filters.id.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("id")} />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>Name
                <FaFilter className={`filter-icon ${filters.name.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("name")} />
                {showFilterPopup === "name" && renderFilterPopup("name")}
              </th>
              <th style={{ position: "relative" }}>Email
                <FaFilter className={`filter-icon ${filters.email.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("email")} />
                {showFilterPopup === "email" && renderFilterPopup("email")}
              </th>
              <th style={{ position: "relative" }}>Mobile
                <FaFilter className={`filter-icon ${filters.mobile.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("mobile")} />
                {showFilterPopup === "mobile" && renderFilterPopup("mobile")}
              </th>
              <th style={{ position: "relative" }}>Company
                <FaFilter className={`filter-icon ${filters.company.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("company")} />
                {showFilterPopup === "company" && renderFilterPopup("company")}
              </th>
              <th style={{ position: "relative" }}>Date
                <FaFilter className={`filter-icon ${filters.date.length > 0 ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("date")} />
                {showFilterPopup === "date" && renderFilterPopup("date")}
              </th>
              <th>View</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedContacts.map((contact) => (
              <tr key={contact.id}
                className={selectedRows.includes(contact.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(contact.id)}>
                <td className="hide-column">
                  <input type="checkbox" checked={selectedRows.includes(contact.id)}
                    onChange={() => toggleRowSelection(contact.id)} />
                </td>
                <td>{contact.id}</td>
                <td>{contact.name}</td>
                <td>{contact.email}</td>
                <td>{contact.mobile}</td>
                <td>{contact.company}</td>
                <td>{contact.date}</td>
                <td>
                  <button className="action-button" onClick={() => viewContact(contact)}>
                    <GrDocumentText />
                  </button>
                </td>
                <td className="actions">
                  <button className={`action-button ${selectedRows.length > 1 ? "disabled-icon" : ""}`}
                    onClick={() => deleteContact(contact.id)}
                    disabled={selectedRows.length > 1}>
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(v) => { setCurrentPage(1); setItemsPerPage(v); }} />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" /> ({selectedRows.length})
        </button>
      )}

      {showModal && selectedContact && (
        <ContactModal contact={selectedContact} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

// Modal Component
function ContactModal({ contact, onClose }) {
  const renderField = (key, label) => {
    if (key === "file" && contact.file) {
      const fileUrl = `${BASE_URL}${contact.file}`;
      return (
        <tr>
          <td>{label}</td>
          <td>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#3085d6" }}>
              {contact.file.split('/').pop()}
            </a>
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td>{label}</td>
        <td>{contact[key] || "N/A"}</td>
      </tr>
    );
  };

  return (
    <div className="job-modal-overlay" onClick={onClose}>
      <div className="job-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-modal-header">
          <h2>Contact Form Details (ID: {contact.id})</h2>
          <button className="job-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <table className="job-details-table">
          <tbody>
            {renderField("id", "ID")}
            {renderField("name", "Name")}
            {renderField("email", "Email")}
            {renderField("mobile", "Mobile")}
            {renderField("company", "Company")}
            <tr>
              <td>Message</td>
              <td style={{ whiteSpace: "pre-wrap", maxWidth: "500px" }}>
                {contact.message || "N/A"}
              </td>
            </tr>
            {renderField("file", "Attached File")}
            {renderField("date", "Date")}
          </tbody>
        </table>
      </div>
    </div>
  );
}